import { ButtonView, ButtonViewContainer, Container, FilterChips } from '@components/Filter';
import { FilterButton } from '@components/Filter/FilterButton';
import { FilterDrawer } from '@components/Filter/FilterDrawer';
import { hasValue } from '@components/Filter/filter.utils';
import { FlexRow } from '@components/FlexRow';
import StarIcon from '@components/Icon/Star';
import StarActiveIcon from '@components/Icon/StarActiveIcon';
import InputCheckboxGroup from '@components/Input/InputCheckboxGroup';
import InputSliderRangeStatic from '@components/Input/InputSliderRange/static';
import InputAutocomplete from '@components/InputAutocomplete';
import { SearchBarWithNumber } from '@components/SearchbarWithNumber';
import JobApplicationFilterStatus from '@modules/jobApplications/components/JobApplicationFilterStatus';
import { useJobApplicationFilters } from '@modules/jobApplications/hooks/useJobApplicationFilters';
import { jobApplicationStatusLabels } from '@modules/jobApplications/statuses';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Box, Button, CircularProgress, FormGroup, FormLabel, IconButton, Rating, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactNode, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { renderValue } from '../../components/Filter/filter.utils';

export const ButtonCustom = styled(Button)(({ theme }) => ({
    background: 'none',
    border: `1px solid ${theme.palette.secondary.main}`,
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    padding: '12px 24px',
    maxHeight: '48px',
}));

/** Custom label renderer for JobApplication filter chips */
const renderJobApplicationChipLabel = ({
    key,
    value,
    label,
}: {
    key: string;
    value: any;
    label: string;
}): ReactNode | undefined => {
    // Handle job application status with translated labels
    if (key === 'jobApplicationStatus') {
        const translatedValue = jobApplicationStatusLabels[value as keyof typeof jobApplicationStatusLabels] ?? value;
        return renderValue({ label, key, value: translatedValue });
    }

    // Return undefined to use default rendering
    return undefined;
};

type CityFilterOption = {
    label: string;
    value: string;
    address?: {
        postalCode?: string | number;
    };
};

export type IJobApplicationDetailFilter = {
    talentId?: string[];
    proId?: string[];
    jobNumber?: string[];
    jobApplicationStatus?: string[];
    salary?: {
        min: number | null;
        max: number | null;
    };
    locations?: CityFilterOption[];
    jobOfferLocations?: CityFilterOption[];
    workingHours?: {
        min: number | null;
        max: number | null;
    };
    sectorIds?: string[];
    yearsOfExperience?: {
        min: number | null;
        max: number | null;
    };
    language?: string;
    createdBy?: string;
    brandIds?: string[];
    createdAt?: {
        start: string;
        end: string;
    };
    publishedDate?: {
        start: string;
        end: string;
    };
    jobApplicationType?: string;
};

export const defaultJobApplicationFilters: IJobApplicationDetailFilter = {
    talentId: [],
    proId: [],
    salary: {
        min: null,
        max: null,
    },
    locations: [],
    jobOfferLocations: [],
    workingHours: {
        min: null,
        max: null,
    },
    jobApplicationStatus: [],
    jobApplicationType: '',
    sectorIds: [],
    yearsOfExperience: {
        min: null,
        max: null,
    },
    language: '',
    createdBy: '',
    brandIds: [],
    createdAt: {
        start: null,
        end: null,
    },
    publishedDate: {
        start: null,
        end: null,
    },
};

export const filterLabels = {
    proId: 'Établissement',
    jobApplicationStatus: 'Statut de candidature',
    salary: 'Salary',
    locations: `CP, ville, département, région`,
    jobOfferLocations: 'Ville de l\'offre',
    workingHours: 'Working hours/week',
    sectorIds: 'Secteur d’activité',
    experience: 'Expérience',
    language: 'Language',
    createdBy: 'Created by',
    brandIds: 'Marque',
    createdAt: 'Created Date',
    publishedDate: 'Published Date',
    function: 'Fonction',
    rating: 'Score',
    contractTypes: 'Type de contrat',
    distance: `Distance`,
    diplomas: 'Diplômes',
    travail: 'Lieu de travail',
};

export const JobApplicationDetailFilter = ({
    viewMode,
    totalDocs,
    changeViewMode,
    onQueryChange,
    onSortChange,
    onFilterChange,
    filter,
    hideChipKeys = [],
    onRefresh,
    initialQuery,
}: {
    viewMode: string;
    totalDocs: number;
    changeViewMode: (event: React.MouseEvent<HTMLButtonElement>) => void;
    onQueryChange: (query: string) => void;
    onSortChange: (sort: { field: string; order: number }) => void;
    onFilterChange: (filter: IJobApplicationDetailFilter) => void;
    filter?: IJobApplicationDetailFilter;
    hideChipKeys?: string[];
    onRefresh?: (proId?: string) => void | Promise<void>;
    initialQuery?: string;
}) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [searchQuery, setSearchQuery] = useState(initialQuery ?? '');
    const [isRefreshing, setIsRefreshing] = useState(false);

    const methods = useForm({
        defaultValues: defaultJobApplicationFilters,
    });

    const { availableFilters, filtersLoading } = useJobApplicationFilters(filter ?? defaultJobApplicationFilters, searchQuery);

    /** Append " (N)" count to each option label */
    const withCount = useMemo(
        () =>
            (opts: { value: string; label: string; count: number; groupLabel?: string }[]) =>
                opts.map(o => ({ value: o.value, label: `${o.label} (${o.count})`, groupLabel: o.groupLabel })),
        [],
    );

    const toggleDrawer = open => () => {
        setIsDrawerOpen(open);
    };

    const onApplyFilter = data => {
        onFilterChange(data);
        setIsDrawerOpen(false);
    };

    const onResetFilter = () => {
        onFilterChange(defaultJobApplicationFilters);
        methods.reset(defaultJobApplicationFilters);
        setIsDrawerOpen(false);
    };

    const handleQueryChange = (q: string) => {
        setSearchQuery(q);
        onQueryChange(q);
    };

    const hasVisibleActiveFilters = Object.entries(filter || {}).some(
        ([key, value]) => !hideChipKeys.includes(key) && hasValue(value),
    );

    return (
        <FormProvider {...methods}>
            <Container sx={{ marginBottom: '0px', overflowX: 'auto', overflowY: 'hidden' }}>
                <FlexRow>
                    <SearchBarWithNumber onChange={handleQueryChange} placeholder="Recherche..." totalCount={totalDocs} initialValue={initialQuery} />
                    <FilterButton toggleDrawer={toggleDrawer} />
                    {onRefresh && (
                        <Tooltip title="Rafraîchir les données">
                            <span>
                                <IconButton
                                    onClick={async () => {
                                        setIsRefreshing(true);
                                        try {
                                            await onRefresh();
                                        } finally {
                                            setIsRefreshing(false);
                                        }
                                    }}
                                    disabled={isRefreshing}
                                    size="small"
                                    sx={{ color: theme => theme.palette.primary.main }}
                                >
                                    {isRefreshing ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </FlexRow>
                <FlexRow>
                    <ButtonViewContainer>
                        <ButtonView onClick={changeViewMode} value="list" activeView={viewMode === 'list'}>
                            <i className="fi fi-br-list" />
                        </ButtonView>
                        <ButtonView disabled onClick={changeViewMode} value="grid" activeView={viewMode === 'grid'}>
                            <i className="fi fi-br-grid" />
                        </ButtonView>
                        <ButtonView disabled onClick={changeViewMode} value="kanban" activeView={viewMode === 'kanban'}>
                            <i className="fi fi-br-chart-kanban" />
                        </ButtonView>
                    </ButtonViewContainer>
                    <JobApplicationFilterStatus
                        value={filter?.jobApplicationStatus ?? []}
                        onChange={statuses =>
                            onFilterChange({
                                ...(filter ?? defaultJobApplicationFilters),
                                jobApplicationStatus: statuses,
                            })
                        }
                    />
                </FlexRow>
            </Container>
            {hasVisibleActiveFilters && (
                <Container>
                    <FlexRow>
                        <FilterChips
                            filter={filter}
                            labels={filterLabels}
                            defaultFilters={defaultJobApplicationFilters}
                            onFilterChange={onFilterChange}
                            hideKeys={hideChipKeys}
                            renderLabel={renderJobApplicationChipLabel}
                        />
                    </FlexRow>
                </Container>
            )}
            <FilterDrawer
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                onApplyFilter={onApplyFilter}
                onResetFilter={onResetFilter}>
                <FormGroup>
                    <FormLabel>{filterLabels.rating}</FormLabel>
                    <Box
                        sx={{
                            padding: '8px 16px',
                            borderRadius: '16px',
                            background: theme => theme.palette.disableFormBlack.main,
                            width: 'fit-content',
                        }}>
                        <Rating
                            value={rating}
                            sx={{ color: '#ffd700', display: 'flex', alignItems: 'center', gap: '14px' }}
                            icon={<StarActiveIcon style={{ width: '24px', height: '24px' }} />}
                            emptyIcon={<StarIcon style={{ width: '24px', height: '24px' }} />}
                            onChange={(event, newValue) => {
                                setRating(newValue);
                            }}
                        />
                    </Box>
                </FormGroup>
                {/* <FormGroup>
                    <FormLabel>{filterLabels.distance}</FormLabel>
                    <InputDistance
                        name="distance"
                        min={0}
                        max={150}
                        defaultValue={0}
                        units="km"
                        minLabel="0 km"
                        maxLabel="150 km"
                    />
                </FormGroup> */}
                <FormGroup>
                    <FormLabel>{filterLabels.locations}</FormLabel>
                    <InputAutocomplete
                        id="locations"
                        name="locations"
                        multiple
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.cities}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.jobOfferLocations}</FormLabel>
                    <InputAutocomplete
                        id="locations"
                        name="jobOfferLocations"
                        multiple
                        getOptionLabel={option => `${option.label} (${option.count})`}
                        options={availableFilters.jobOfferCities}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.proId}</FormLabel>
                    <InputAutocomplete
                        id="proId"
                        name="proId"
                        multiple
                        loading={filtersLoading}
                        options={withCount(availableFilters.proIds)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.contractTypes}</FormLabel>
                    <InputAutocomplete
                        id="contractTypes"
                        name="contractTypes"
                        multiple
                        loading={filtersLoading}
                        options={withCount(availableFilters.contractTypes)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.function}</FormLabel>
                    <InputAutocomplete
                        id="professions"
                        name="position.professions"
                        multiple
                        loading={filtersLoading}
                        groupBy={option => option.groupLabel}
                        getOptionLabel={option => option.groupLabel ? `${option.groupLabel} - ${option.label}` : option.label}
                        options={withCount(availableFilters.professions)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.sectorIds}</FormLabel>
                    <InputAutocomplete
                        id="sectorIds"
                        name="sectorIds"
                        multiple
                        loading={filtersLoading}
                        options={withCount(availableFilters.sectorIds)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.diplomas}</FormLabel>
                    <InputAutocomplete
                        id="diplomas"
                        name="diplomas"
                        multiple
                        loading={filtersLoading}
                        options={withCount(availableFilters.diplomaTypes)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.experience}</FormLabel>
                    <InputSliderRangeStatic
                        name="experience"
                        min={availableFilters.yearsOfExperience?.min ?? 0}
                        max={availableFilters.yearsOfExperience?.max ?? 25}
                        defaultMin={3}
                        defaultMax={10}
                        units="y"
                        minLabel="0 year"
                        maxLabel="25 year"
                        minDistance={0}
                    />
                </FormGroup>
                <FormGroup>
                    <InputCheckboxGroup
                        name="experienceTypes"
                        direction="row"
                        options={[
                            { label: 'Toute année', value: 'allYears' },
                            { label: 'Jeune diplômé', value: 'freshGraduate' },
                        ]}
                    />
                </FormGroup>
                <FormGroup>
                    <FormLabel>{filterLabels.salary}</FormLabel>
                    <InputSliderRangeStatic
                        name="salary"
                        min={availableFilters.salaryRange.min ?? 0}
                        max={availableFilters.salaryRange.max ?? 5000}
                        defaultMin={1000}
                        defaultMax={2000}
                        units="€"
                        minLabel="0€"
                        maxLabel="5 000€"
                        minDistance={100}
                    />
                </FormGroup>
            </FilterDrawer>
        </FormProvider>
    );
};
